import { cn } from '@/lib/cn'

const MOTION_STYLE_ID = 'tunes-progressbar-motion'
const SWEEP =
  '@keyframes tunes-progress-sweep{0%{transform:translateX(-110%)}100%{transform:translateX(360%)}}'

/**
 * The indeterminate sweep needs a keyframe, and the design tokens in
 * styles/index.css are not this component's to edit — so the one motion-only
 * rule it needs is registered here, once. The global prefers-reduced-motion
 * rule still collapses it to a static bar.
 */
function ensureMotionStyles(): void {
  if (typeof document === 'undefined' || document.getElementById(MOTION_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = MOTION_STYLE_ID
  style.textContent = SWEEP
  document.head.append(style)
}

ensureMotionStyles()

export interface ProgressBarProps {
  /** 0-100. Ignored when `indeterminate` is set. */
  value?: number
  indeterminate?: boolean
  /** Accessible name. Required unless the bar is labelled by something else. */
  label?: string
  size?: 'sm' | 'md'
  className?: string
}

export function ProgressBar({
  value = 0,
  indeterminate = false,
  label,
  size = 'sm',
  className,
}: ProgressBarProps) {
  const clamped = Math.max(0, Math.min(100, Number.isFinite(value) ? value : 0))

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={indeterminate ? undefined : Math.round(clamped)}
      aria-valuetext={indeterminate ? 'Working' : `${Math.round(clamped)}%`}
      className={cn(
        'relative w-full overflow-hidden rounded-full bg-surface-hover',
        size === 'sm' ? 'h-1' : 'h-1.5',
        className,
      )}
    >
      {indeterminate ? (
        <div
          className="h-full w-1/3 rounded-full bg-accent"
          style={{ animation: 'tunes-progress-sweep 1.4s ease-in-out infinite' }}
        />
      ) : (
        <div
          className="h-full rounded-full bg-accent transition-[width] duration-300 ease-out"
          style={{ width: `${clamped}%` }}
        />
      )}
    </div>
  )
}
